import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from '@packages/utils';
import { useGetWorkshopByIdQuery } from 'redux/apiSlice/workshops.slice';
import {
  useShowToastNotification,
  ENotificationType,
} from 'components/Base/Notification';
import { RoutesPaths } from 'routes/Routes.types';
import WorkshopStatus from 'components/Shared/Statuses/WorkshopStatus';
import PageWrapper from 'components/Base/PageWrapper';
import { TabsProps } from 'components/Base/Tabs/Tabs.types';
import { Tabs } from 'components/Base/Tabs';
import { EWorkshopStatus, EAttendanceType } from 'data/types/workshops.types';
import { TRtkErrorType } from 'data/types/generic.types';
import { TBackButtonState } from 'components/Base/PageWrapper/PageWrapper.types';
import useKeepTabsInUrl from 'hooks/useKeepTabsInUrl';
import { useIsFeatureEnabled } from 'hooks/useIsFeatureEnabled';
import Notes from 'components/Shared/Notes';
import { ENoteEntityType } from 'data/types/notes.types';
import OutputTab from 'components/Shared/OutputTab';
import useIsRfkAdmin from 'hooks/useIsRfkAdmin';
import Actions from './Actions';
import { PartTimeValidationProvider } from './context/PartTimeValidationContext';
import DetailsTab from './DetailsTab';
import WaitListTab from './WaitlistTab';
import { EWorkShopDetailsTabsUrl } from './WorkShopDetails.types';
import ChildCareTab from './ChildCareTab';
import QuestionTab from './QuestionTab';
import RegistrantsTab from './RegistrantsTab';
import AttendanceTab from './AttendanceTab';
import KitsTab from './KitsTab/KitsTab';
import TasksTab from './TasksTab';

const WorkShopDetails = () => {
  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.workshops',
  });
  const { showToastNotification } = useShowToastNotification();
  const params = useParams();
  const location = useLocation();
  const goBackState = (location.state as TBackButtonState) || {
    backUrl: `/app/${RoutesPaths.WORKSHOP}`,
    backButtonSuffix: t('list.TITLE'),
  };
  const searchParams = new URLSearchParams(location.search);
  const navigate = useNavigate();
  const { id } = params as { id: string };
  const { data, isLoading, isFetching, error } = useGetWorkshopByIdQuery({
    id,
  });
  const isKitsManagementEnabled = useIsFeatureEnabled('kits_management');
  const isRfkAdmin = useIsRfkAdmin();

  useEffect(() => {
    if (location.search.includes('duplicated')) {
      showToastNotification({
        message: t('details.actions.duplicate.SUCCESS_MESSAGE'),
        type: ENotificationType.SUCCESS,
      });
      searchParams.delete('workshopAction');
      navigate(`${location.pathname}?${searchParams.toString()}`);
    }
  }, [location.search]);

  const onBackClick = () => {
    navigate(goBackState.backUrl);
  };

  const isOutputsRoute = location.pathname.endsWith('/inputs-outputs');
  const { tabInUrl } = useKeepTabsInUrl({
    defaultTab: isOutputsRoute
      ? EWorkShopDetailsTabsUrl.OUTPUT
      : EWorkShopDetailsTabsUrl.DETAILS,
    stateInUrl: goBackState,
  });

  const handleTabChange = (key: string) => {
    if (!id) return;
    if (key === EWorkShopDetailsTabsUrl.OUTPUT) {
      navigate(
        `/app/${RoutesPaths.WORKSHOP_DETAILS}/inputs-outputs`.replace(
          ':id',
          id,
        ),
      );
    } else {
      const basePath = `/app/${RoutesPaths.WORKSHOP_DETAILS}`.replace(
        ':id',
        id,
      );
      navigate(`${basePath}?tab=${key}`, { state: goBackState });
    }
  };

  useEffect(() => {
    if (!isRfkAdmin && tabInUrl === EWorkShopDetailsTabsUrl.TASKS) {
      handleTabChange(EWorkShopDetailsTabsUrl.DETAILS);
    }
  }, [isRfkAdmin, tabInUrl]);

  const tabItems: TabsProps['items'] = [
    {
      key: EWorkShopDetailsTabsUrl.DETAILS,
      label: <span data-cy="details_tab">{t('details.tabs.DETAILS')}</span>,
      children: <DetailsTab data={data} isFetching={isFetching} id={id} />,
    },
    {
      key: EWorkShopDetailsTabsUrl.REGISTRANTS,
      label: (
        <span data-cy="registrants_tab">{t('details.tabs.REGISTRANTS')}</span>
      ),
      children: <RegistrantsTab data={data} />,
      show: data?.status !== EWorkshopStatus.PLANNING,
      forceRender: true,
    },
    {
      key: EWorkShopDetailsTabsUrl.ATTENDANCE,
      label: (
        <span data-cy="attendance_tab">{t('details.tabs.ATTENDANCE')}</span>
      ),
      children: <AttendanceTab data={data} />,
      show: data?.status !== EWorkshopStatus.PLANNING,
      forceRender: true,
    },
    {
      key: EWorkShopDetailsTabsUrl.WAITLIST,
      label: <span data-cy="waitlist_tab">{t('details.tabs.WAITLIST')}</span>,
      children: <WaitListTab data={data} />,
      show: data?.status !== EWorkshopStatus.PLANNING,
      forceRender: true,
    },
    {
      key: EWorkShopDetailsTabsUrl.QUESTION,
      label: <span data-cy="question_tab">{t('details.tabs.QUESTION')}</span>,
      children: <QuestionTab question={data?.customQuestion} workshopId={id} />,
      show: !!(
        (data?.status === EWorkshopStatus.PLANNING &&
          !data.customQuestion.allowedOperations.readonly) ||
        data?.customQuestion?.value
      ),
    },
    {
      key: EWorkShopDetailsTabsUrl.CHILD_CARE,
      label: (
        <span data-cy="childcare_tab">{t('details.tabs.CHILD_CARE')}</span>
      ),
      children: <ChildCareTab id={id} />,
      show: !!(
        data?.status !== EWorkshopStatus.PLANNING &&
        data?.childcareProvided?.value
      ),
      forceRender: true,
    },
    {
      key: EWorkShopDetailsTabsUrl.TASKS,
      label: (
        <span data-cy="tasks_tab">{t('details.tabs.TASKS', 'Tasks')}</span>
      ),
      children: <TasksTab workshopId={id} />,
      show: isRfkAdmin,
    },
    {
      key: EWorkShopDetailsTabsUrl.NOTES,
      label: <span data-cy="notes_tab">{t('details.tabs.NOTES')}</span>,
      children: <Notes entityType={ENoteEntityType.WORKSHOP} entityId={id} />,
    },
    {
      key: EWorkShopDetailsTabsUrl.OUTPUT,
      label: (
        <span data-cy="output_tab">{t('details.tabs.OUTPUT', 'Outputs')}</span>
      ),
      children: (
        <OutputTab
          id={id}
          hostId={data?.host?.id}
          locationId={data?.location?.id}
          isVirtual={data?.attendanceType === EAttendanceType.VIRTUAL}
        />
      ),
    },
    {
      key: EWorkShopDetailsTabsUrl.KITS,
      label: <span data-cy="kits_tab">{t('details.tabs.KITS', 'Kits')}</span>,
      children: <KitsTab data={data} />,
      show:
        isKitsManagementEnabled &&
        data?.attendanceType !== EAttendanceType.VIRTUAL,
      forceRender: true,
    },
  ];

  return (
    // Wraps both `actions` and the tabs so the part editors inside DetailsTab can
    // gate the Publish/Republish buttons on an unsaved End <= Start (GROW-4212).
    <PartTimeValidationProvider>
      <PageWrapper
        title={data?.host.name}
        titleSuffix={<WorkshopStatus className="ml-2" text={data?.status} />}
        {...(data && {
          backButton: {
            labelPrefix: data?.host.name,
            tooltip: goBackState.backButtonSuffix,
            onClick: onBackClick,
          },
        })}
        actions={<Actions id={id} data={data} />}
        isLoading={isLoading}
        error={data ? undefined : (error as TRtkErrorType)}
      >
        <Tabs
          defaultActiveKey={tabInUrl}
          onChange={handleTabChange}
          items={tabItems}
          destroyInactiveTabPane
        />
      </PageWrapper>
    </PartTimeValidationProvider>
  );
};

export default WorkShopDetails;
